import { useState } from 'react';
import { adminApi } from '../../api/adminApi';

interface FeatureCheckboxProps {
  userId: number;
  userLabel: string;
  featureCode: string;
  initiallyChecked: boolean;
  disabled?: boolean;
  onChanged: (granted: boolean) => void;
  onToast: (message: string) => void;
  onPropagationWarning: (userLabel: string) => void;
}

// Feature 011 — controlled checkbox tied to grant/revoke. Surfaces a toast on
// success and dispatches the FR-028 "picks this change up on next login"
// inline cue via onPropagationWarning.
export function FeatureCheckbox({
  userId,
  userLabel,
  featureCode,
  initiallyChecked,
  disabled,
  onChanged,
  onToast,
  onPropagationWarning,
}: FeatureCheckboxProps) {
  const [checked, setChecked] = useState(initiallyChecked);
  const [saving, setSaving] = useState(false);

  const handleToggle = async () => {
    if (saving || disabled) return;
    const nextValue = !checked;
    setSaving(true);
    setChecked(nextValue);
    try {
      if (nextValue) {
        await adminApi.grantFeature(userId, featureCode);
        onToast(`Granted ${featureCode}`);
      } else {
        await adminApi.revokeFeature(userId, featureCode);
        onToast(`Revoked ${featureCode}`);
      }
      onChanged(nextValue);
      onPropagationWarning(userLabel);
    } catch {
      setChecked(!nextValue);
      onToast('Failed to update grant.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled || saving}
      onChange={handleToggle}
      aria-label={`${featureCode} for ${userLabel}`}
      className="rounded border-line-strong text-primary-600 focus:ring-primary-500 disabled:opacity-50"
    />
  );
}
